import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { CALCULATOR_PRODUCTS, getProduct } from "@/lib/products";
import { calcEmi, inrFull, inrShort } from "@/lib/format";
import { MoneyInput, MoneyWords } from "@/components/MoneyInput";

const AnimatedMoney = ({ value, className, testId }) => {
  const spring = useSpring(value, { stiffness: 110, damping: 24 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const text = useTransform(spring, (v) => inrFull(v));
  return (
    <motion.span data-testid={testId} className={className}>
      {text}
    </motion.span>
  );
};

const Donut = ({ principalShare }) => {
  const r = 64;
  const C = 2 * Math.PI * r;
  const principalLen = (principalShare / 100) * C;
  return (
    <svg viewBox="0 0 160 160" className="h-44 w-44 -rotate-90" data-testid="emi-donut-chart">
      <circle cx="80" cy="80" r={r} fill="none" stroke="#B8913A" strokeWidth="18" />
      <circle
        cx="80" cy="80" r={r} fill="none" stroke="#DCE5F2" strokeWidth="18"
        strokeDasharray={`${principalLen} ${C - principalLen}`}
        strokeLinecap="butt"
        style={{ transition: "stroke-dasharray 0.6s cubic-bezier(0.22,1,0.36,1)" }}
      />
    </svg>
  );
};

export const EMICalculator = ({ initialProduct }) => {
  const [slug, setSlug] = useState(initialProduct || "personal-loan");
  const product = getProduct(slug) || CALCULATOR_PRODUCTS[0];
  const [amount, setAmount] = useState(product.amountDefault);
  const [rate, setRate] = useState(Math.round((product.rateMin + 0.15) * 100) / 100);
  const [months, setMonths] = useState(product.tenureDefault);
  const [unit, setUnit] = useState("years");

  const switchProduct = (nextSlug) => {
    const p = getProduct(nextSlug);
    if (!p || p.inCalculators === false) return;
    setSlug(nextSlug);
    setAmount(p.amountDefault);
    setRate(Math.round((p.rateMin + 0.15) * 100) / 100);
    setMonths(p.tenureDefault);
  };

  const amountNum = Number(amount) || 0;
  const sliderAmount = Math.min(product.amountMax, Math.max(product.amountMin, amountNum || product.amountMin));

  const { emi, totalInterest, totalPayment, principalShare } = useMemo(
    () => calcEmi(amountNum, rate, months),
    [amountNum, rate, months]
  );

  const tenureDisplay = unit === "years" ? Math.round((months / 12) * 10) / 10 : months;
  const applyParams = `/apply?product=${slug}&amount=${amountNum}&rate=${rate}&tenure=${months}&emi=${Math.round(emi)}`;

  const clampRate = (v) => Math.min(product.rateMax, Math.max(product.rateMin, v || product.rateMin));

  return (
    <div data-testid="emi-calculator" className="grid overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_30px_80px_rgba(20,40,75,0.1)] lg:grid-cols-5">
      {/* Controls */}
      <div className="p-6 sm:p-10 lg:col-span-3">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Loan product">
          {CALCULATOR_PRODUCTS.map((p) => (
            <button
              key={p.slug}
              role="tab"
              aria-selected={slug === p.slug}
              data-testid={`emi-tab-${p.slug}`}
              onClick={() => switchProduct(p.slug)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 sm:text-sm ${
                slug === p.slug
                  ? "bg-concordia text-ivory shadow-[0_8px_20px_rgba(20,40,75,0.3)]"
                  : "border border-sand bg-cream text-charcoal/70 hover:border-concordia/40 hover:text-concordia"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className="mt-10 space-y-10">
          <div>
            <div className="mb-3 flex items-end justify-between gap-4">
              <label htmlFor="emi-amount-input" className="text-sm font-medium text-slate">Loan amount</label>
              <MoneyInput
                id="emi-amount-input"
                testId="emi-amount-input"
                wordsTestId="emi-amount-words"
                value={amount}
                max={product.amountMax}
                onChange={(v) => setAmount(v === "" ? "" : Math.min(product.amountMax, v))}
                onBlur={() => setAmount((a) => Math.min(product.amountMax, Math.max(product.amountMin, Number(a) || product.amountMin)))}
              />
            </div>
            <Slider
              data-testid="emi-amount-slider"
              value={[sliderAmount]}
              min={product.amountMin}
              max={product.amountMax}
              step={product.amountStep}
              onValueChange={([v]) => setAmount(v)}
              className="slider-brand"
            />
            <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
              <span>{inrShort(product.amountMin)}</span>
              <span>{inrShort(product.amountMax)}</span>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-end justify-between gap-4">
              <label htmlFor="emi-rate-input" className="text-sm font-medium text-slate">Interest rate (p.a.)</label>
              <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                <input
                  id="emi-rate-input"
                  data-testid="emi-rate-input"
                  type="number"
                  step="0.05"
                  value={rate}
                  min={product.rateMin}
                  max={product.rateMax}
                  onChange={(e) => setRate(clampRate(Number(e.target.value)))}
                  className="w-16 bg-transparent text-right font-semibold text-concordia outline-none"
                />
                <span className="ml-1 font-semibold text-concordia">%</span>
              </div>
            </div>
            <Slider
              data-testid="emi-rate-slider"
              value={[rate]}
              min={product.rateMin}
              max={product.rateMax}
              step={0.05}
              onValueChange={([v]) => setRate(v)}
              className="slider-brand"
            />
            <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
              <span>{product.rateMin}%</span>
              <span>{product.rateMax}%</span>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-end justify-between gap-4">
              <label htmlFor="emi-tenure-input" className="text-sm font-medium text-slate">Tenure</label>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                  <input
                    id="emi-tenure-input"
                    data-testid="emi-tenure-input"
                    type="number"
                    value={tenureDisplay}
                    min={1}
                    onChange={(e) => {
                      const v = Number(e.target.value) || 1;
                      setMonths(unit === "years" ? Math.round(v * 12) : Math.round(v));
                    }}
                    className="w-16 bg-transparent text-right font-semibold text-concordia outline-none"
                  />
                </div>
                <div className="flex overflow-hidden rounded-xl border border-sand" data-testid="emi-tenure-unit-switch">
                  {["years", "months"].map((u) => (
                    <button
                      key={u}
                      data-testid={`emi-tenure-unit-${u}`}
                      onClick={() => setUnit(u)}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                        unit === u ? "bg-concordia text-ivory" : "bg-cream text-charcoal/60 hover:text-concordia"
                      }`}
                    >
                      {u === "years" ? "Yr" : "Mo"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Slider
              data-testid="emi-tenure-slider"
              value={[months]}
              min={product.tenureMin}
              max={product.tenureMax}
              step={1}
              onValueChange={([v]) => setMonths(v)}
              className="slider-brand"
            />
            <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
              <span>{product.tenureMin} mo</span>
              <span>{product.tenureMax} mo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="relative flex flex-col justify-between bg-concordia-ink p-6 text-ivory sm:p-10 lg:col-span-2">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="relative">
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">Your monthly EMI</p>
          <AnimatedMoney
            value={emi}
            testId="emi-value"
            className="mt-3 block text-4xl font-bold tracking-tight text-ivory sm:text-5xl"
          />
          <MoneyWords value={emi} suffix="Rupees per month" dark testId="emi-value-words" />
          <p className="mt-2 text-xs text-ivory/50">
            {inrShort(amountNum)} · {rate}% p.a. · {months} months
          </p>

          <div className="mt-8 flex items-center gap-6">
            <div className="relative">
              <Donut principalShare={principalShare} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-serif text-2xl font-semibold text-ivory" data-testid="emi-principal-share">
                  {Math.round(principalShare)}%
                </span>
                <span className="text-[0.6rem] uppercase tracking-widest text-ivory/50">principal</span>
              </div>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <div className="flex items-center gap-2 text-ivory/60">
                  <span className="h-2.5 w-2.5 rounded-sm bg-sage" aria-hidden="true" />
                  <span className="text-xs">Principal</span>
                </div>
                <AnimatedMoney value={amountNum} testId="emi-principal-amount" className="mt-0.5 block font-semibold" />
                <MoneyWords value={amountNum} dark testId="emi-principal-words" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-ivory/60">
                  <span className="h-2.5 w-2.5 rounded-sm bg-brass" aria-hidden="true" />
                  <span className="text-xs">Total interest</span>
                </div>
                <AnimatedMoney value={totalInterest} testId="emi-total-interest" className="mt-0.5 block font-semibold" />
                <MoneyWords value={totalInterest} dark testId="emi-interest-words" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-ivory/60">
                  <span className="h-2.5 w-2.5 rounded-sm border border-champagne" aria-hidden="true" />
                  <span className="text-xs">Total payable</span>
                </div>
                <AnimatedMoney value={totalPayment} testId="emi-total-payment" className="mt-0.5 block font-semibold text-brass" />
                <MoneyWords value={totalPayment} dark testId="emi-total-words" />
              </div>
            </div>
          </div>
        </div>

        <Link
          to={applyParams}
          data-testid="emi-apply-button"
          className="group relative mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-ivory px-7 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]"
        >
          Apply with these numbers
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};
